import { useState } from "react";
import { useRouter } from "next/router";
import classNames from "classnames/bind";
import { Size as SizeType } from "../types";
import { MinusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/MinusOutlined";
import { PlusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlusOutlined";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { SelectorAtom } from "@JohnLewisPartnership/jl-design-system/dist/components/selector-list/SelectorAtom";
import { SelectorList } from "@JohnLewisPartnership/jl-design-system/dist/components/selector-list/SelectorList";
import { sanitizeHref } from "../utils/sanitizeHref";
import { getSizeOfferLabel } from "../utils/getSizeOfferLabel";
import styles from "./SizeItems.module.scss";

const cx = classNames.bind(styles);

type SizeItemsProps = {
  sizes: SizeType[];
  showMore?: number;
  groupName?: string;
  replaceHistoryEntryOnVariantChange: boolean;
  desktopColumns: number;
  mobileColumns: number;
  selectedSizeValue?: string;
  onSizeSelected: (selectedSizeValue: string) => void;
};

export const SizeItems = ({
  sizes,
  showMore,
  groupName,
  replaceHistoryEntryOnVariantChange,
  desktopColumns,
  mobileColumns,
  selectedSizeValue,
  onSizeSelected,
}: SizeItemsProps) => {
  const [isOpen, setOpen] = useState(false);
  const router = useRouter();
  const visibleSizes = sizes.filter(
    (size, index) =>
      isOpen || size.isSelected || (showMore && index < showMore) || !showMore,
  );
  const hiddenItems = sizes.length - visibleSizes.length;
  const isButtonVisible = !!showMore && ((!isOpen && !!hiddenItems) || isOpen);
  const handleSizeChange = (value: string) => {
    const selectedSize = visibleSizes.find((size) => size.id === value);

    if (!selectedSize) {
      throw new Error(`Unknown size selection: ${value}`);
    }

    onSizeSelected(value);
    const sanitizedHref = sanitizeHref(selectedSize.url);

    if (replaceHistoryEntryOnVariantChange) {
      void router.replace(sanitizedHref, undefined, {
        shallow: true,
        scroll: false,
      });
      return;
    }

    void router.push(sanitizedHref, undefined, {
      shallow: true,
      scroll: false,
    });
  };

  return (
    <li className={cx("group", { reducedMargin: !isButtonVisible })}>
      {groupName && <h5 className={styles.heading}>{groupName}</h5>}
      <div className={styles.groupSizesList} data-testid="group:sizes:list">
        <SelectorList
          ariaLabel={groupName ? `Select a ${groupName} size` : "Select a size"}
          desktopColumns={desktopColumns}
          maxVisibleAtoms={visibleSizes.length}
          mobileColumns={mobileColumns}
          onChange={handleSizeChange}
          value={selectedSizeValue ?? ""}
        >
          {visibleSizes.map((size, index) => (
            <SelectorAtom
              className={styles.size}
              key={`size-${index}`}
              isOutOfStock={size.isOutOfStock ?? size.unavailable}
              label={size.name}
              outOfStockLabel="currently unavailable"
              offerLabel={getSizeOfferLabel(size)}
              value={size.id}
            />
          ))}
        </SelectorList>
      </div>
      {isButtonVisible && (
        <TertiaryButton
          onClick={() => setOpen((toggle) => !toggle)}
          icon={isOpen ? MinusOutlined : PlusOutlined}
          className={styles.showMore}
          labelText={isOpen ? "Show less" : `Show ${hiddenItems} more`}
        />
      )}
    </li>
  );
};
